import { Divider, Switcher } from "pepsico-ds";
import PropTypes from "prop-types";
import ButtonGroupDynamic from "../../../../../components/common/ButtonGroupDynamic";

function stripHtml(html) {
  if (!html || typeof html !== "string") return html;
  if (!/<[a-z][\s\S]*>/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent?.trim() || html;
}

export default function SurveyConditionDisplay({
  ruleData,
  onLogicOperatorChange,
  onEdit,
  onDelete,
  readOnly = false,
}) {
  const conditionDisplay = ruleData?.condition_display;

  if (!conditionDisplay?.selections?.length) {
    return null;
  }

  const { survey_name, selections } = conditionDisplay;
  const logicOperator = ruleData?.logic_operator || "and";
  const showToggle = selections.length > 1;
  const logicOptions = ["AND", "OR"];
  const selectedLogicIndex = logicOptions.indexOf(logicOperator.toUpperCase());

  const formatAnswerDisplayText = (selection) => {
    const texts = selection.selected_option_texts || [
      selection.selected_option_text,
    ];
    const isMultiple = texts.length > 1;
    const quotedTexts = texts.map((text) => `"${stripHtml(text)}"`).join(", ");
    return isMultiple
      ? `Answer is one of ${quotedTexts}`
      : `Answer is equal to ${quotedTexts}`;
  };

  return (
    <div className="survey-condition-display" data-testid="survey-condition-display">
      <Divider
        style={{ margin: "16px 0", backgroundColor: "rgba(0, 92, 188, 0.2)" }}
      />
      <div className="survey-condition-display__header">
        <div className="survey-condition-display__header-left">
          <span className="survey-condition-display__survey-name">
            {survey_name}
          </span>
          <span className="survey-condition-display__badge">Survey Condition</span>
        </div>
        {!readOnly && (
          <ButtonGroupDynamic
            size="small"
            items={[
              { icon: "edit", onClick: onEdit },
              { icon: "delete", onClick: onDelete },
            ]}
          />
        )}
      </div>

      <div className="survey-condition-display__selections">
        {selections.map((selection, index) => (
          <div key={selection.question_id}>
            <div
              className="survey-condition-display__selection"
              data-testid={`condition-selection-${selection.question_id}`}
            >
              <span className="survey-condition-display__arrow">↳</span>
              <span className="survey-condition-display__question">
                {selection.question_title}
              </span>
              <span className="survey-condition-display__separator">·</span>
              <span className="survey-condition-display__answer">
                {formatAnswerDisplayText(selection)}
              </span>
            </div>

            {showToggle && index < selections.length - 1 && (
              <div className="toggle-divider-container">
                {readOnly ? (
                  <>
                    <span
                      className="survey-condition-display__logic-label"
                      data-testid="logic-operator-label"
                    >
                      {logicOperator.toUpperCase()}
                    </span>
                    <Divider style={{ margin: "16px 0" }} />
                  </>
                ) : (
                  <>
                    <div className="survey-condition-display__toggle">
                      <Switcher
                        items={logicOptions}
                        onUpdate={(index) =>
                          onLogicOperatorChange(logicOptions[index].toLowerCase())
                        }
                        selectedItemIndex={selectedLogicIndex}
                        data-testid="logic-toggle-switcher"
                      />
                    </div>
                    <Divider style={{ margin: "16px 0" }} />
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

SurveyConditionDisplay.propTypes = {
  ruleData: PropTypes.shape({
    logic_operator: PropTypes.string,
    answer_options: PropTypes.arrayOf(PropTypes.string),
    condition_display: PropTypes.shape({
      survey_id: PropTypes.string,
      survey_name: PropTypes.string,
      selections: PropTypes.arrayOf(
        PropTypes.shape({
          question_id: PropTypes.string,
          question_title: PropTypes.string,
          selected_option_ids: PropTypes.arrayOf(PropTypes.string),
          selected_option_texts: PropTypes.arrayOf(PropTypes.string),
          selected_option_id: PropTypes.string,
          selected_option_text: PropTypes.string,
        })
      ),
    }),
  }),
  readOnly: PropTypes.bool,
  onLogicOperatorChange: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

SurveyConditionDisplay.defaultProps = {
  ruleData: null,
};
